import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input, Textarea } from "@/components/ui/field";
import { returnableUnits, type OrderDetail } from "@bizy/core";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useReturnOrder } from "./api";

export interface ReturnEquipmentDialogProps {
  open: boolean;
  onClose: () => void;
  workspaceId: string;
  order: OrderDetail;
}

const unitsPerItem = (order: OrderDetail): Record<string, number> => {
  const result: Record<string, number> = {};
  for (const item of order.items) {
    // Sold goods are consumed on checkout and never come back.
    if (item.product_kind === "sale") {
      result[item.id] = 0;
      continue;
    }

    const rows = order.movements.filter((movement) => movement.order_item_id === item.id);
    const sum = (reason: "reserve" | "release" | "write_off") =>
      rows
        .filter((movement) => movement.reason === reason)
        .reduce((total, movement) => total + movement.line_units, 0);

    result[item.id] = returnableUnits({
      reserved_units: sum("reserve"),
      released_units: sum("release"),
      written_off_units: sum("write_off"),
    });
  }
  return result;
};

/** Units still on rent across the whole order; drives the page-level button. */
export const orderOutstandingUnits = (order: OrderDetail): number =>
  Object.values(unitsPerItem(order)).reduce((total, units) => total + units, 0);

interface Draft {
  good: string;
  damaged: string;
}

const EMPTY_DRAFT: Draft = { good: "0", damaged: "0" };

/**
 * Rental return: undamaged units go back to the shelf, damaged ones are written
 * off the ledger. The Worker validates against the stock movements, so a client
 * cannot return more than it reserved.
 */
export const ReturnEquipmentDialog = ({
  open,
  onClose,
  workspaceId,
  order,
}: ReturnEquipmentDialogProps) => {
  const outstanding = useMemo(() => unitsPerItem(order), [order]);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [note, setNote] = useState("");
  const returnOrder = useReturnOrder(workspaceId, order.id);

  useEffect(() => {
    if (!open) return;
    setDrafts({});
    setNote("");
  }, [open]);

  const onSubmit = async () => {
    const lines = Object.entries(drafts)
      .map(([orderItemId, draft]) => ({
        order_item_id: orderItemId,
        quantity: Math.max(0, Math.trunc(Number(draft.good) || 0)),
        damaged_quantity: Math.max(0, Math.trunc(Number(draft.damaged) || 0)),
      }))
      .filter((line) => line.quantity + line.damaged_quantity > 0);

    if (lines.length === 0) {
      toast.error("Enter at least one returned unit.");
      return;
    }

    try {
      await returnOrder.mutateAsync({
        note: note.trim().length > 0 ? note.trim() : null,
        lines,
      });
      toast.success("Return recorded and stock restored.");
      onClose();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not record the return.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Return equipment"
      description="Undamaged units go back to stock. Damaged units are written off and cannot be rented again."
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={returnOrder.isPending} onClick={() => void onSubmit()}>
            Record return
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {order.items.map((item) => {
          const available = outstanding[item.id] ?? 0;
          const draft = drafts[item.id] ?? EMPTY_DRAFT;

          return (
            <div key={item.id} className="space-y-2 rounded-lg border border-border p-3">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <p className="text-sm font-medium">
                    {item.name_snapshot}
                    {item.variant_snapshot ? (
                      <span className="text-muted-foreground"> · {item.variant_snapshot}</span>
                    ) : null}
                  </p>
                  {item.bundle_components?.length ? (
                    <p className="text-xs text-muted-foreground">
                      {item.bundle_components
                        .map((component) => `${component.name} × ${component.quantity}`)
                        .join(" · ")}
                    </p>
                  ) : null}
                </div>
                <span className="shrink-0 text-xs text-muted-foreground">
                  {available} outstanding
                </span>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Field label="Returned good" htmlFor={`return-good-${item.id}`}>
                  <Input
                    id={`return-good-${item.id}`}
                    inputMode="numeric"
                    disabled={available === 0}
                    value={draft.good}
                    onChange={(event) =>
                      setDrafts((current) => ({
                        ...current,
                        [item.id]: { ...draft, good: event.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="Damaged" htmlFor={`return-damaged-${item.id}`}>
                  <Input
                    id={`return-damaged-${item.id}`}
                    inputMode="numeric"
                    disabled={available === 0}
                    value={draft.damaged}
                    onChange={(event) =>
                      setDrafts((current) => ({
                        ...current,
                        [item.id]: { ...draft, damaged: event.target.value },
                      }))
                    }
                  />
                </Field>
              </div>
            </div>
          );
        })}

        <Field label="Note" htmlFor="return-note" hint="Condition, missing accessories, …">
          <Textarea
            id="return-note"
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </Field>
      </div>
    </Dialog>
  );
};
